import { Component, ElementRef, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { MessageService } from 'src/app/services/message.service';
import {
  AdminUsersGQL,
  AdminUsersQuery,
  RegisterAdminUsersGQL,
  RegisterAdminUsersMutation,
  ResetAdminUserPasswordGQL,
  DeleteAdminUserGQL,
  UpdateAdminUserFunctionsGQL,
} from 'src/gql/generated';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { USER_FUNCTIONS, UserFunctionDef } from 'src/app/constants/user-functions';

type AdminUser = AdminUsersQuery['adminUsers'][number];
type RegisteredAdminUser = RegisterAdminUsersMutation['registerAdminUsers']['users'][number];

interface UserGroupOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-admin-user',
  templateUrl: './admin-user.component.html',
  styleUrls: ['./admin-user.component.sass'],
  standalone: true,
  imports: [
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule,
    DialogModule,
    Textarea,
    CheckboxModule,
    TooltipModule,
    ConfirmDialogModule,
    TagModule,
    InputTextModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService],
})
export class AdminUserComponent implements OnInit {
  // ユーザグループ選択肢（ハードコード）
  userGroups: UserGroupOption[] = [
    { id: 1, name: 'ののコン' },
    { id: 8, name: '渚' },
  ];

  // 権限選択肢
  functionOptions: UserFunctionDef[] = USER_FUNCTIONS;

  // デフォルトでチェックする権限（ドンキ・イオン以外）
  private readonly defaultFunctions = ['share', 'kojima', 'biccamera', 'spreadsheet', 'discontinue'];

  // デフォルト値
  private readonly DEFAULT_SERVICE_TYPE = 'nono';
  private readonly DEFAULT_USER_ROLE = 'billing';

  // 一覧データ
  users: AdminUser[] = [];
  filteredUsers: AdminUser[] = [];
  isLoading = false;
  selectedGroupId: number | null = null;
  searchText = '';

  // 登録ダイアログ
  showRegisterDialog = false;
  registerGroupId: number | null = null;
  registerFunctions: string[] = [];
  registerUsersText = '';

  // 登録結果ダイアログ
  showResultDialog = false;
  registeredUsers: RegisteredAdminUser[] = [];

  // パスワード変更結果ダイアログ
  showPasswordDialog = false;
  resetPasswordResult: { userId: number; password: string } | null = null;

  // 権限編集ダイアログ
  showEditFunctionsDialog = false;
  editTargetUser: AdminUser | null = null;
  editFunctions: string[] = [];

  constructor(
    private elementRef: ElementRef,
    private routingService: RoutingService,
    private subtitleService: SubtitleService,
    private userService: UserService,
    private message: MessageService,
    private confirmationService: ConfirmationService,
    private adminUsersGQL: AdminUsersGQL,
    private registerAdminUsersGQL: RegisterAdminUsersGQL,
    private resetAdminUserPasswordGQL: ResetAdminUserPasswordGQL,
    private deleteAdminUserGQL: DeleteAdminUserGQL,
    private updateAdminUserFunctionsGQL: UpdateAdminUserFunctionsGQL,
  ) {
    this.routingService.loginRequired();
    this.userService.userRequired();
    this.subtitleService.setSubTitle('ユーザ管理');
  }

  async ngOnInit() {
    await this.loadUsers();
  }

  async loadUsers() {
    this.isLoading = true;
    try {
      const resp = await firstValueFrom(
        this.adminUsersGQL.fetch({
          variables: {
            userGroupId: this.selectedGroupId,
          },
        }),
      );
      this.users = resp.data.adminUsers.filter((u) => !u.isDeleted);
      this.applySearch();
    } catch (error) {
      this.message.setFlashMessageWithClear('error', 'エラー', 'ユーザ一覧の取得に失敗しました');
    } finally {
      this.isLoading = false;
    }
  }

  onGroupFilterChange() {
    this.loadUsers();
  }

  onSearchChange() {
    this.applySearch();
  }

  private applySearch() {
    const keyword = this.searchText.trim().toLowerCase();
    if (!keyword) {
      this.filteredUsers = this.users;
      return;
    }
    this.filteredUsers = this.users.filter(
      (u) => u.email.toLowerCase().includes(keyword) || (u.name && u.name.toLowerCase().includes(keyword)),
    );
  }

  // 登録ダイアログ
  openRegisterDialog() {
    this.registerGroupId = null;
    this.registerFunctions = [...this.defaultFunctions];
    this.registerUsersText = '';
    this.showRegisterDialog = true;
  }

  canRegister(): boolean {
    return !!this.registerGroupId && !!this.registerUsersText.trim();
  }

  async doRegister() {
    if (!this.canRegister()) {
      return;
    }

    // 入力をパース（1行につき「名前, メール」形式）
    const lines = this.registerUsersText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);

    const users: { name: string; email: string; functions?: string[] }[] = [];
    const invalidLines: string[] = [];

    for (const line of lines) {
      const parts = line.split(',').map((p) => p.trim());
      if (parts.length >= 2 && parts[0] && parts[1]) {
        users.push({
          name: parts[0],
          email: parts[1],
          functions: this.registerFunctions.length > 0 ? this.registerFunctions : undefined,
        });
      } else {
        invalidLines.push(line);
      }
    }

    if (invalidLines.length > 0) {
      this.message.setFlashMessageWithClear(
        'warn',
        '入力形式エラー',
        `以下の行が不正です:\n${invalidLines.join('\n')}`,
      );
      return;
    }

    if (users.length === 0) {
      this.message.setFlashMessageWithClear('warn', '入力エラー', 'ユーザ情報を入力してください');
      return;
    }

    this.isLoading = true;
    try {
      const resp = await firstValueFrom(
        this.registerAdminUsersGQL.mutate({
          variables: {
            input: {
              userGroupId: this.registerGroupId!,
              serviceType: this.DEFAULT_SERVICE_TYPE,
              userRole: this.DEFAULT_USER_ROLE,
              users,
            },
          },
        }),
      );

      if (resp.data?.registerAdminUsers.success) {
        this.registeredUsers = resp.data.registerAdminUsers.users;
        this.showRegisterDialog = false;
        this.showResultDialog = true;
        await this.loadUsers();
      } else {
        this.message.setFlashMessageWithClear(
          'error',
          '登録エラー',
          resp.data?.registerAdminUsers.message || '登録に失敗しました',
        );
      }
    } catch (error) {
      this.message.setFlashMessageWithClear('error', 'エラー', '登録に失敗しました');
    } finally {
      this.isLoading = false;
    }
  }

  closeResultDialog() {
    this.showResultDialog = false;
    this.registeredUsers = [];
  }

  downloadRegisteredUsersCsv() {
    const csv = this.registeredUsers.map((u) => `${u.userId},${u.email},${u.password}`).join('\n');
    const header = 'ユーザID,メール,パスワード\n';
    this.outputCsv(header + csv, 'registered_users.csv');
  }

  // パスワード変更
  confirmResetPassword(user: AdminUser) {
    this.confirmationService.confirm({
      message: `${user.name || user.email} のパスワードを変更しますか？`,
      header: 'パスワード変更確認',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: '変更する',
      rejectLabel: 'キャンセル',
      accept: () => this.doResetPassword(user),
    });
  }

  async doResetPassword(user: AdminUser) {
    this.isLoading = true;
    try {
      const resp = await firstValueFrom(
        this.resetAdminUserPasswordGQL.mutate({
          variables: {
            userId: user.id,
          },
        }),
      );

      if (resp.data?.resetAdminUserPassword.success) {
        this.resetPasswordResult = {
          userId: resp.data.resetAdminUserPassword.userId,
          password: resp.data.resetAdminUserPassword.password,
        };
        this.showPasswordDialog = true;
      } else {
        this.message.setFlashMessageWithClear(
          'error',
          'エラー',
          resp.data?.resetAdminUserPassword.message || 'パスワード変更に失敗しました',
        );
      }
    } catch (error) {
      this.message.setFlashMessageWithClear('error', 'エラー', 'パスワード変更に失敗しました');
    } finally {
      this.isLoading = false;
    }
  }

  closePasswordDialog() {
    this.showPasswordDialog = false;
    this.resetPasswordResult = null;
  }

  // ユーザ削除
  confirmDeleteUser(user: AdminUser) {
    this.confirmationService.confirm({
      message: `${user.name || user.email} を削除しますか？この操作は取り消せません。`,
      header: '削除確認',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: '削除する',
      rejectLabel: 'キャンセル',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.doDeleteUser(user),
    });
  }

  async doDeleteUser(user: AdminUser) {
    this.isLoading = true;
    try {
      const resp = await firstValueFrom(
        this.deleteAdminUserGQL.mutate({
          variables: {
            userId: user.id,
          },
        }),
      );

      if (resp.data?.deleteAdminUser.success) {
        this.message.setFlashMessageWithClear('success', '削除成功', 'ユーザを削除しました', 3000);
        await this.loadUsers();
      } else {
        this.message.setFlashMessageWithClear(
          'error',
          'エラー',
          resp.data?.deleteAdminUser.message || '削除に失敗しました',
        );
      }
    } catch (error) {
      this.message.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
    } finally {
      this.isLoading = false;
    }
  }

  // 権限編集ダイアログ
  openEditFunctionsDialog(user: AdminUser) {
    this.editTargetUser = user;
    this.editFunctions = [...user.functions];
    this.showEditFunctionsDialog = true;
  }

  async doUpdateFunctions() {
    if (!this.editTargetUser) {
      return;
    }

    this.isLoading = true;
    try {
      const resp = await firstValueFrom(
        this.updateAdminUserFunctionsGQL.mutate({
          variables: {
            userId: this.editTargetUser.id,
            functions: this.editFunctions,
          },
        }),
      );

      if (resp.data?.updateAdminUserFunctions.success) {
        this.message.setFlashMessageWithClear('success', '更新成功', '権限を更新しました', 3000);
        this.showEditFunctionsDialog = false;
        await this.loadUsers();
      } else {
        this.message.setFlashMessageWithClear(
          'error',
          'エラー',
          resp.data?.updateAdminUserFunctions.message || '権限の更新に失敗しました',
        );
      }
    } catch (error) {
      this.message.setFlashMessageWithClear('error', 'エラー', '権限の更新に失敗しました');
    } finally {
      this.isLoading = false;
    }
  }

  // ロール表示用
  getRoleLabel(role: string): string {
    const roleMap: { [key: string]: string } = {
      admin: '管理者',
      billing: '課金',
      free: '無料',
    };
    return roleMap[role] || role;
  }

  getRoleSeverity(role: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const severityMap: { [key: string]: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' } = {
      admin: 'danger',
      billing: 'info',
      free: 'secondary',
    };
    return severityMap[role] || 'secondary';
  }

  // 権限表示用（単一）
  getFunctionLabel(func: string): string {
    const opt = this.functionOptions.find((o) => o.value === func);
    return opt ? opt.label : func;
  }

  // 権限のTag色
  getFunctionSeverity(func: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const severityMap: { [key: string]: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' } = {
      share: 'info',
      kojima: 'success',
      biccamera: 'warn',
      rakuten: 'danger',
      iris: 'success',
      spreadsheet: 'secondary',
      donki: 'contrast',
      discontinue: 'warn',
      aeon: 'danger',
      openClose: 'secondary',
      user: 'contrast',
      amazonPriceTracking: 'info',
      keepa: 'success',
    };
    return severityMap[func] || 'secondary';
  }

  // 権限表示用（カンマ区切り - CSV用）
  getFunctionLabels(functions: string[]): string {
    if (!functions || functions.length === 0) {
      return '-';
    }
    return functions.map((f) => this.getFunctionLabel(f)).join(', ');
  }

  // CSV出力
  private outputCsv(csv: string, filename: string) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const elem = this.elementRef.nativeElement;
    const link = elem.querySelector('#csv-download') as HTMLAnchorElement;
    if (link) {
      link.href = url;
      link.download = filename;
      link.click();
    }
  }
}
